// /customers — Customer List (PAGES.md §3.29)
//
// Full CRM list with search, RFM filter and CSV export. The hero mirrors the
// Reports Dashboard language so the admin app feels cohesive.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  PageContainer,
  ErrorBoundary,
  Button,
  DataTable,
  Badge,
  Avatar,
  EmptyState,
  Skeleton,
  RfmTagBadge,
  useToast,
  FilterBar,
  SavedFiltersMenu,
  ListSummaryBar,
} from '@bbpos/ui';
import type { ColumnDef, FilterBarFacet } from '@bbpos/ui';
import type { CurrencyCode } from '@bbpos/types';
import { formatPaise } from '@/utils/currency';
import {
  customersApi,
  useCustomerList,
  RFM_SEGMENT_ORDER,
  segmentMeta,
  formatPhone,
  formatShortDate,
} from '@/features/customers';
import type { Customer, RfmSegment } from '@/features/customers';
import { MembershipTierPill } from '@/features/memberships';
import type { MembershipTier } from '@/features/memberships';
import { useListFilterBar } from '@/features/saved-filters';
import { downloadFile } from '@/lib/download-file';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';

const SEGMENT_OPTIONS = RFM_SEGMENT_ORDER.map((s) => ({ value: s, label: segmentMeta(s)!.label }));

// T27: the shared filter bar. Keys are the list endpoint's own parameters.
export const CUSTOMER_FILTER_KEYS = ['name', 'mobile', 'email', 'search', 'segment', 'period', 'active', 'start_date', 'end_date'];
// Status (CU1-004): no value = Active, the API's default.
const STATUS_OPTIONS = [
  { value: 'false', label: 'Inactive' },
  { value: 'all', label: 'Active and inactive' },
];
const CUSTOMER_FACETS: FilterBarFacet[] = [
  { key: 'name', label: 'Name' },
  { key: 'mobile', label: 'Mobile', kind: 'phone' },
  { key: 'email', label: 'Email' },
  { key: 'search', label: 'Name, mobile or email' },
];
// Joined on: the business's days (customers belong to no one store).
const CUSTOMER_DATE = { fromKey: 'start_date', toKey: 'end_date', label: 'Joined' };

type Row = Customer;

export function makeColumns(currency: CurrencyCode): ColumnDef<Row>[] {
  return [
  {
    key: 'name',
    header: 'Customer',
    render: (_v, row) => (
      <div className="flex items-center gap-3">
        <Avatar name={row.name} size="sm" />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 truncate">{row.name}</p>
          {row.email ? <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate">{row.email}</p> : null}
        </div>
      </div>
    ),
  },
  {
    key: 'phone',
    header: 'Phone',
    render: (_v, row) => <span className="text-sm text-neutral-700 dark:text-neutral-300 font-mono tabular-nums">{formatPhone(row.phone)}</span>,
  },
  {
    key: 'rfm_segment',
    header: 'RFM',
    render: (_v, row) =>
      row.rfm_segment ? (
        <RfmTagBadge tag={row.rfm_segment} size="sm" />
      ) : (
        <span className="text-xs text-neutral-400">—</span>
      ),
  },
  {
    key: 'membership_tier',
    header: 'Membership',
    render: (_v, row) =>
      row.membership_tier ? (
        <MembershipTierPill tier={row.membership_tier as MembershipTier} size="sm" />
      ) : (
        <span className="text-xs text-neutral-400">—</span>
      ),
  },
  {
    key: 'loyalty_points',
    header: 'Loyalty',
    numeric: true,
    render: (_v, row) => (
      <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 font-mono tabular-nums">
        {row.loyalty_points.toLocaleString('en-IN')}
      </span>
    ),
  },
  {
    key: 'last_visit_at',
    header: 'Last Visit',
    render: (_v, row) => <span className="text-sm text-neutral-600 dark:text-neutral-300 font-mono tabular-nums">{formatShortDate(row.last_visit_at)}</span>,
  },
  {
    key: 'total_spent',
    header: 'Total Spent',
    numeric: true,
    render: (_v, row) => <span className="text-sm font-bold text-neutral-900 dark:text-neutral-100 font-mono tabular-nums">{formatPaise(row.total_spent, currency)}</span>,
  },
  {
    key: 'is_active',
    header: 'Status',
    render: (_v, row) => (
      <Badge variant={row.is_active ? 'success' : 'voided'} size="sm">
        {row.is_active ? 'Active' : 'Inactive'}
      </Badge>
    ),
  },
  {
    key: 'actions',
    header: 'Actions',
    render: (_v, row) => (
      <div className="flex items-center justify-end gap-2">
        <Link
          to={`/customers/${row.id}`}
          aria-label={`View ${row.name}`}
          className="inline-flex items-center rounded-md bg-violet-50 px-2.5 py-1 text-xs font-semibold text-violet-700 ring-1 ring-violet-200 transition-colors hover:bg-violet-100 dark:bg-violet-900/30 dark:text-violet-200 dark:ring-violet-700"
        >
          View
        </Link>
        <Link
          to={`/customers/${row.id}?edit=1`}
          aria-label={`Edit ${row.name}`}
          className="inline-flex items-center rounded-md bg-neutral-100 px-2.5 py-1 text-xs font-semibold text-neutral-700 ring-1 ring-neutral-200 transition-colors hover:bg-neutral-200 dark:bg-neutral-700 dark:text-neutral-100 dark:ring-neutral-600"
        >
          Edit
        </Link>
      </div>
    ),
  },
  ];
}

function CustomerListContent() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const currency = useTenantCurrency();
  const columns = useMemo(() => makeColumns(currency), [currency]);
  const searchRef = useRef<HTMLInputElement>(null);
  const [isExporting, setIsExporting] = useState(false);

  const bar = useListFilterBar({ page: 'customers', keys: CUSTOMER_FILTER_KEYS, date: CUSTOMER_DATE });
  const { values } = bar;
  const page = Number(searchParams.get('page') ?? '1');
  const segment = (values.segment ?? '') as RfmSegment | '';
  const active = (values.active ?? '') as '' | 'false' | 'all';

  const listParams = {
    search: values.search,
    name: values.name,
    mobile: values.mobile,
    email: values.email,
    start_date: values.start_date,
    end_date: values.end_date,
    segment,
    // The RFM Dashboard's window, carried by its View Customers link (CD16).
    period: values.period,
    active,
  };
  const { data, isLoading, isFetching, error } = useCustomerList({ ...listParams, page });

  const rows = useMemo<Row[]>(() => data?.data ?? [], [data]);

  // Keyboard shortcuts: / focus search, n new customer
  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === '/') {
        e.preventDefault();
        searchRef.current?.focus();
      } else if (e.key === 'n') {
        e.preventDefault();
        navigate('/customers/new');
      }
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [navigate]);

  // Every matching customer, all pages — the API writes the file (QA 7.1.22, T27).
  const handleExport = async () => {
    if (!data?.meta.total) {
      toast({ variant: 'warning', title: 'Nothing to export', description: 'The current view has no customers.' });
      return;
    }
    setIsExporting(true);
    try {
      downloadFile(await customersApi.exportCsv(listParams), 'customers.csv');
    } catch (err) {
      toast({ variant: 'error', title: 'Export failed', description: (err as Error).message || 'Please try again.' });
    } finally {
      setIsExporting(false);
    }
  };

  const totalCount = data?.meta.total ?? 0;

  return (
    <div className="animate-fade-in">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-600 via-violet-600 to-fuchsia-600 p-6 sm:p-7 text-white mb-6 shadow-[0_25px_70px_-25px_rgba(139,92,246,0.6)]">
        <div className="pointer-events-none absolute -top-24 -right-16 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-16 -left-10 h-48 w-48 rounded-full bg-black/10 blur-3xl" />
        <div className="relative flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-[10px] font-bold uppercase tracking-wider ring-1 ring-white/25 backdrop-blur-sm">
              CRM
            </span>
            <h1 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight">Customers</h1>
            <p className="mt-1.5 text-sm text-white/85 max-w-xl">
              Every customer in your CRM with RFM segmentation, loyalty and lifetime value at a glance.
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
              <span className="rounded-full bg-white/15 backdrop-blur-sm px-3 py-1 font-semibold tabular-nums">
                {totalCount.toLocaleString('en-IN')} total
              </span>
              {segment ? (
                <span className="rounded-full bg-white/15 backdrop-blur-sm px-3 py-1 font-semibold">
                  Filter: {segmentMeta(segment)?.label}
                  {values.period ? ` · last ${values.period.replace('d', ' days')}` : ''}
                </span>
              ) : null}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="secondary"
              onClick={() => void handleExport()}
              disabled={isExporting}
              className="bg-white/15 hover:bg-white/25 text-white border-white/30 backdrop-blur-sm"
            >
              {isExporting ? 'Exporting…' : 'Export CSV'}
            </Button>
            <Button variant="primary" onClick={() => navigate('/customers/new')} className="!bg-white !text-violet-700 hover:!bg-white/90 !border-transparent">
              + Add Customer
            </Button>
          </div>
        </div>
      </div>

      {/* Filter bar (T27) */}
      <div className="mb-4">
        <FilterBar
          facets={CUSTOMER_FACETS}
          selects={[
            { key: 'segment', label: 'Segment', options: SEGMENT_OPTIONS, allLabel: 'All segments' },
            { key: 'active', label: 'Status', options: STATUS_OPTIONS, allLabel: 'Active' },
          ]}
          date={CUSTOMER_DATE}
          values={values}
          onChange={bar.onChange}
          onReset={bar.onReset}
          timeZone={bar.timeZone}
          inputRef={searchRef}
          placeholder="Type a name, mobile or email (press / to focus)"
          favorites={<SavedFiltersMenu {...bar.favorites} />}
        />
      </div>

      {/* Every matching customer, not the page — the export's count and sum. */}
      <div className="mb-6">
        <ListSummaryBar
          loading={isLoading}
          items={[
            { label: 'Customers matching', value: totalCount.toLocaleString('en-IN'), tone: 'violet' },
            { label: 'Total spent', value: formatPaise(data?.meta.summary?.total_spent ?? 0, currency), tone: 'success' },
          ]}
        />
      </div>

      {/* Data */}
      {error ? (
        <div className="rounded-xl bg-danger-50 dark:bg-danger-900/20 shadow-md p-4 text-sm text-danger-700 dark:text-danger-300">
          Failed to load customers. Please refresh.
        </div>
      ) : isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 10 }).map((_, i) => (
            <Skeleton key={i} variant="rect" height={64} className="rounded-lg" />
          ))}
        </div>
      ) : rows.length === 0 && !isFetching ? (
        <div className="rounded-2xl bg-white dark:bg-neutral-800 shadow-md p-10 animate-fade-in">
          <EmptyState
            variant="page"
            title="No customers yet"
            description="Customer profiles are created automatically during billing, or you can add one manually."
            action={{ label: '+ Add Customer', onClick: () => navigate('/customers/new') }}
            icon={
              <svg className="h-20 w-20 text-violet-300 dark:text-violet-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m3-6a4 4 0 11-8 0 4 4 0 018 0zm6 0a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            }
          />
        </div>
      ) : (
        <section className="rounded-2xl bg-white dark:bg-neutral-800 shadow-md ring-1 ring-neutral-100/80 dark:ring-neutral-700/60 overflow-hidden animate-slide-up">
          <DataTable<Row>
            columns={columns}
            data={rows}
            loading={isFetching}
            totalCount={data?.meta.total}
            currentPage={page}
            pageSize={20}
            onPageChange={(p) => bar.onChange({ page: String(p) })}
            stickyHeader
          />
        </section>
      )}
    </div>
  );
}

export function CustomerListPage() {
  return (
    <PageContainer title="Customers" breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Customers' }]}>
      <ErrorBoundary resetKey="customers-list">
        <CustomerListContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
